import Decimal from 'decimal.js';
import { useCallback, useMemo, useState } from 'react';

import { currencies, defaults, getCurrencyConfig, periods, widgetConfig } from '../../config/config';
import { Currency } from '../../types/currency';
import { parseCurrencyOptions, resolveInitialCurrency } from '../../utils/urlParams';
import { SLIDER_MIDPOINT } from '../utils/calculatorConfig';
import {
  collateralBtcToLoan,
  loanToCollateralBtc,
  maturityRepayment,
} from '../utils/calculatorMath';
import { calculateOptimalRate } from '../../utils/calculateOptimalRate';
import { checkInstantAvailability } from '../../utils/checkInstantAvailability';
import { useChanceToMatch } from '../../hooks/useChanceToMatch';
import { useIsMobile } from './useIsMobile';

interface Options {
  initialCurrency?: string;
  currencyOptions?: string;
  initialAmount?: string;
  initialPeriod?: string;
}

const BTC_DECIMALS = 5;
const FIAT_DECIMALS = 0;

function parseAmount(raw: string): Decimal | null {
  // Strip thousands separators (commas, spaces, narrow-no-break-space).
   
  const cleaned = raw.replace(/[,\s  ]/g, '');
  if (cleaned === '' || cleaned === '.') return null;
  try {
    const value = new Decimal(cleaned);
    return value.lt(0) ? null : value;
  } catch {
    return null;
  }
}

const FIAT_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: FIAT_DECIMALS,
  minimumFractionDigits: 0,
});

function formatFiat(value: Decimal): string {
  return FIAT_FORMATTER.format(Number(value.toFixed(FIAT_DECIMALS)));
}

function formatBtc(value: Decimal): string {
  return value.toFixed(BTC_DECIMALS);
}

function resolveInitialPeriod(raw?: string): number {
  if (!raw) return defaults.period;
  const n = Number(raw);
  return Number.isInteger(n) && periods.includes(n) ? n : defaults.period;
}

export function useLoanCalculator({
  initialCurrency,
  currencyOptions,
  initialAmount,
  initialPeriod,
}: Options) {
  const isMobile = useIsMobile();
  const { computeChanceToMatch } = useChanceToMatch();
  const availableCurrencies = useMemo(
    () => parseCurrencyOptions(currencyOptions, currencies) ?? currencies,
    [currencyOptions]
  );
  const [currency, setCurrencyState] = useState<Currency>(() =>
    resolveInitialCurrency(initialCurrency, availableCurrencies, defaults.currency)
  );
  const [period, setPeriod] = useState<number>(() => resolveInitialPeriod(initialPeriod));

  const config = useMemo(() => getCurrencyConfig(currency)!, [currency]);
  const btcPrice = useMemo(() => new Decimal(config.bitcoinPrice), [config]);
  const minLoan = useMemo(() => new Decimal(config.minTransactionLimit), [config]);
  const maxLoan = useMemo(() => new Decimal(config.maxTransactionLimit), [config]);
  const minBtc = useMemo(() => loanToCollateralBtc(minLoan, btcPrice), [minLoan, btcPrice]);
  const maxBtc = useMemo(() => loanToCollateralBtc(maxLoan, btcPrice), [maxLoan, btcPrice]);
  // BTC slider midpoint = the loan slider's midpoint converted to BTC, so the
  // two handles stay vertically aligned at every position (a geometric-mean
  // anchor would drift up to ~1.6% of track width off the loan handle).
  const midBtc = useMemo(
    () => loanToCollateralBtc(SLIDER_MIDPOINT[currency], btcPrice),
    [currency, btcPrice]
  );

  // Authoritative loan amount as Decimal. Collateral is always derived.
  const [loanBig, setLoanBig] = useState<Decimal>(() => {
    // Seed from the ?amount param when valid; else a nicer-looking default.
    const parsed = initialAmount ? parseAmount(initialAmount) : null;
    const seed = parsed ?? minLoan.mul(15);
    return seed.lt(minLoan) ? minLoan : seed.gt(maxLoan) ? maxLoan : seed;
  });
  const [loanInput, setLoanInput] = useState<string>(() => formatFiat(loanBig));
  const [collateralInput, setCollateralInput] = useState<string>(() =>
    formatBtc(loanToCollateralBtc(loanBig, btcPrice))
  );

  const updateFromLoan = useCallback(
    (next: Decimal) => {
      const clamped = next.lt(minLoan) ? minLoan : next.gt(maxLoan) ? maxLoan : next;
      setLoanBig(clamped);
      setLoanInput(formatFiat(clamped));
      setCollateralInput(formatBtc(loanToCollateralBtc(clamped, btcPrice)));
    },
    [minLoan, maxLoan, btcPrice]
  );

  const updateFromCollateral = useCallback(
    (nextBtc: Decimal) => {
      const clampedBtc = nextBtc.lt(minBtc) ? minBtc : nextBtc.gt(maxBtc) ? maxBtc : nextBtc;
      const loan = collateralBtcToLoan(clampedBtc, btcPrice);
      setLoanBig(loan);
      setLoanInput(formatFiat(loan));
      setCollateralInput(formatBtc(clampedBtc));
    },
    [minBtc, maxBtc, btcPrice]
  );

  const onLoanInputChange = useCallback((raw: string) => {
    // remove all non-digit chars
    const digitsOnly = raw.replace(/\D/g, '');
    const parsed = parseAmount(digitsOnly);
    if (parsed && parsed.gt(maxLoan)) {
      setLoanBig(maxLoan);
      setLoanInput(formatFiat(maxLoan));
      setCollateralInput(formatBtc(loanToCollateralBtc(maxLoan, btcPrice)));
      return;
    }
    // Route the display string through the Decimal-typed formatter so the only
    // number-domain crossing is at Intl.NumberFormat inside formatFiat.
    setLoanInput(parsed === null ? '' : formatFiat(parsed));
    if (parsed === null) return;
    setLoanBig(parsed);
    setCollateralInput(formatBtc(loanToCollateralBtc(parsed, btcPrice)));
  }, [btcPrice, maxLoan]);

  const onCollateralInputChange = useCallback((raw: string) => {
    // allow only digits and one decimal separator
    const digitsAndDots = raw.replace(/[^\d.]/g, '');
    const [intPart, ...rest] = digitsAndDots.split('.');
    const filtered = rest.length === 0 ? intPart : `${intPart}.${rest.join('')}`;
    const parsed = parseAmount(filtered);
    if (parsed && parsed.gt(maxBtc)) {
      const loan = collateralBtcToLoan(maxBtc, btcPrice);
      setLoanBig(loan);
      setLoanInput(formatFiat(loan));
      setCollateralInput(formatBtc(maxBtc));
      return;
    }
    setCollateralInput(filtered);
    if (parsed === null) return;
    const loan = collateralBtcToLoan(parsed, btcPrice);
    setLoanBig(loan);
    setLoanInput(formatFiat(loan));
  }, [btcPrice, maxBtc]);

  const onCollateralBlur = useCallback(() => {
    const parsed = parseAmount(collateralInput);
    updateFromCollateral(parsed ?? minBtc);
  }, [collateralInput, minBtc, updateFromCollateral]);

  const setCurrency = useCallback(
    (next: Currency) => {
      setCurrencyState(next);
      const nextConfig = getCurrencyConfig(next)!;
      const nextBtcPrice = new Decimal(nextConfig.bitcoinPrice);
      const nextMin = new Decimal(nextConfig.minTransactionLimit);
      const nextMax = new Decimal(nextConfig.maxTransactionLimit);
      const clamped = loanBig.lt(nextMin) ? nextMin : loanBig.gt(nextMax) ? nextMax : loanBig;
      setLoanBig(clamped);
      setLoanInput(formatFiat(clamped));
      setCollateralInput(formatBtc(loanToCollateralBtc(clamped, nextBtcPrice)));
    },
    [loanBig]
  );

  // Interest rate = the optimal rate from the matching model, capped at the
  // period's instant ticket rate when one is available for the amount — the
  // rate you'd actually get, via ticket or market (no market undercut here,
  // unlike the widget). Depends on amount/period/currency, so it tracks the
  // slider.
  const interestRate = useMemo(() => {
    const amount = loanBig.toFixed(0);
    const ticket = config.investTickets.find(t => t.period === period);
    const instant = checkInstantAvailability({
      instantRate: ticket?.interestRate ?? null,
      amount,
      maxInstantTransactionLimit: config.maxInstantTransactionLimit,
      availableTicketAmount: ticket?.amount ?? '0',
      availableInstantPeriods: config.investTickets.map(t => t.period),
    });
    const rate = calculateOptimalRate({
      amount,
      period,
      currency,
      minRate: parseFloat(config.minInterestRate),
      maxRate: parseFloat(config.maxInterestRate),
      availableInstantRate: instant.available ? ticket?.interestRate : undefined,
      computeChanceToMatch,
      target: 'highestMedium',
      instantRateDiscount: 0,
    });
    return new Decimal(rate);
  }, [loanBig, period, currency, config, computeChanceToMatch]);

  const collateralBig = useMemo(
    () => loanToCollateralBtc(loanBig, btcPrice),
    [loanBig, btcPrice]
  );

  const maturityRepaymentValue = useMemo(
    () => maturityRepayment(loanBig, interestRate, period),
    [loanBig, interestRate, period]
  );

  const onLoanBlur = useCallback(() => {
    const parsed = parseAmount(loanInput);
    updateFromLoan(parsed ?? minLoan);
  }, [loanInput, minLoan, updateFromLoan]);

  return {
    // state (display)
    currency,
    loanInput,
    collateralInput,
    period,
    isMobile,

    // authoritative
    availableCurrencies,
    loanBig,
    collateralBig,
    btcPrice,
    config,
    minLoan,
    maxLoan,
    minBtc,
    midBtc,
    maxBtc,
    periods,

    // outputs
    interestRate,
    maturityRepaymentValue,
    originationFeeRate: widgetConfig.originationFeeRate,

    // actions
    setCurrency,
    setPeriod,
    onLoanInputChange,
    onLoanBlur,
    onCollateralInputChange,
    onCollateralBlur,
    setLoanFromSlider: updateFromLoan,
    setCollateralFromSlider: updateFromCollateral,
  };
}
